// ---------------------------------------------------------------------------
// DecisionsToolbarItem — sino global de "inputs pendentes" (P-E, leva 57-C).
// ---------------------------------------------------------------------------
// Consome ``GET /api/v3/pending-summary/`` (cross-periodo agregado) e
// expoe o total unificado de decisoes pendentes + inputs nao enviados.
// O nome do arquivo ficou historico ("decisions"); a rotulagem atual em
// UI e "inputs pendentes".
//
//   - Icone ``Notifications`` no floating toolbar.
//   - Badge vermelho com a contagem de pendentes (Material-UI ``Badge``
//     injetado pelo ``FloatingToolbarItem`` via prop ``badge``).
//   - Clique navega para ``Modeling > Inputs V3`` (rota
//     ``/main/inputs_v3``), onde as decisoes sao resolvidas no
//     proprio fluxo de cada input.
//
// Escopo desta leva (57-C):
//   - Apenas o sino + badge + click-to-navigate. O dropdown com lista
//     detalhada de decisoes (com resolucao inline) e a integracao com
//     o card dentro do drawer ficam para levas futuras (precisam do
//     POST .../answer/ e de uma decisao de produto sobre o que fazer
//     quando uma decisao e resolvida no caminho que nao passa pelo
//     drawer do input especifico).
//
// Comportamento defensivo:
//   - Se a implementacao ativa nao esta na allowlist da V3 (backend
//     devolve 403 ``inputs_v3_not_enabled``), tratamos como "zero
//     pendentes" silenciosamente. O sino aparece apagado (sem badge)
//     e o clique ainda navega — nao quebra o header para usuarios que
//     nao tem V3.
//   - Polling compartilhado com a toolbar de manutencao via endpoint
//     consolidado ``/api/v3/toolbar-summary/``.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
// Icone: ``CloudUpload`` em vez de sino (``Notifications``) por decisao
// editorial — as decisoes pendentes da V3 quase sempre estao ligadas a
// inputs que o usuario precisa agir (reenviar arquivo, confirmar
// ambiguidade de parse, responder divergencia de cruzamento). O simbolo
// de upload comunica melhor o contexto de "arquivo de entrada" do que
// um sino generico de notificacao.
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import FloatingToolbarItem from "./FloatingToolbarItem";
import useInputsV3ToolbarSummary from "./useInputsV3ToolbarSummary";

export default function DecisionsToolbarItem({ _mobile, _position, ...rest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const toolbarSummary = useInputsV3ToolbarSummary();
  // ``total`` = decisoes pendentes + inputs nao enviados (ate o mes atual).
  // Ver backend ``InputsV3PendingSummaryView``.
  const count = Number(toolbarSummary.pendingSummary?.total) || 0;

  const handleClick = () => {
    setTooltipOpen(false);
    // ``state.focusPendingDecisions`` e lido pelo InputsV3 para (1)
    // abrir automaticamente o MES MAIS ANTIGO que tem decisoes
    // pendentes do usuario (em vez do comportamento padrao de abrir
    // no mes mais recente) e (2) marcar a intencao da navegacao
    // (util para analytics futuro).
    navigate("/main/inputs_v3", { state: { focusPendingDecisions: true } });
  };

  // Singular/plural manuais — i18next plural detection nao esta
  // habilitado consistentemente no projeto. Rotulo atual: "inputs
  // pendentes" (inclui decisoes + inputs nao enviados).
  let tooltipTitle;
  if (count <= 0) {
    tooltipTitle = t("navbar.pending_inputs_none", "Sem pendências");
  } else if (count === 1) {
    tooltipTitle = t("navbar.pending_inputs_singular", "1 input pendente");
  } else {
    tooltipTitle = t(
      "navbar.pending_inputs_plural",
      "{{count}} inputs pendentes",
      { count },
    );
  }

  return (
    <FloatingToolbarItem
      icon={<CloudUploadIcon fontSize="inherit" />}
      mobileLabel={tooltipTitle}
      badge={count}
      tooltipTitle={tooltipTitle}
      tooltipOpen={tooltipOpen}
      onTooltipOpen={() => setTooltipOpen(true)}
      onTooltipClose={() => setTooltipOpen(false)}
      onClick={handleClick}
      _position={_position}
      _mobile={_mobile}
      {...rest}
    />
  );
}

DecisionsToolbarItem.propTypes = {
  _mobile: PropTypes.bool,
  _position: PropTypes.string,
};
